import React, { useState, useEffect, useCallback, useContext } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Clear as ClearIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import KanbanStage from "./KanbanStage";
import KanbanSettingsDialog from "./KanbanSettingsDialog";
import NewStageDialog from "./NewStageDialog";
import { i18n } from "../../translate/i18n";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    overflow: "hidden"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    minHeight: 40 // Garantindo altura mínima mesmo sem botões
  },
  filtersContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1)
  },
  filterInput: {
    margin: theme.spacing(0.5),
    minWidth: 150,
  },
  searchInput: {
    margin: theme.spacing(0.5),
    minWidth: 220,
  },
  filterChip: {
    margin: theme.spacing(0.5),
  },
  boardContainer: {
    display: "flex",
    overflowX: "auto",
    overflowY: "hidden",
    height: "calc(100% - 120px)", // Ajustado para considerar os filtros
    padding: theme.spacing(1)
  },
  stagesContainer: {
    display: "flex",
    height: "100%"
  },
  actionButtons: {
    "& > *": {
      margin: theme.spacing(1),
    },
  }
}));

const KanbanBoard = ({ queueId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [stages, setStages] = useState([]);
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kanbanSettings, setKanbanSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newStageOpen, setNewStageOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [uniqueTags, setUniqueTags] = useState([]);

  // Estados para os filtros
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/kanban/settings", {
        params: { queueId }
      });

      if (data && data.length > 0) {
        const queueSettings = data.find(s => s.queueId === +queueId);
        setKanbanSettings(queueSettings || null);
      } else {
        setKanbanSettings(null);
      }
    } catch (err) {
      console.error(err);
      toast.success(i18n.t("kanban.notifications.settingsLoadError"));

    }
  }, [queueId]);

  const fetchStages = useCallback(async () => {
    try {
      const { data } = await api.get("/kanban/stages", {
        params: { queueId }
      });

      // Sort stages by position
      const sortedStages = data.sort((a, b) => a.position - b.position);
      console.log(`[DEBUG] Estágios encontrados: ${sortedStages.length}`);

      // Exibindo informações detalhadas sobre os estágios
      if (sortedStages.length > 0) {
        sortedStages.forEach(stage => {
          console.log(`[DEBUG] Detalhes do estágio: id=${stage.id}, name=${stage.name}, tipo=${typeof stage.id}`);
        });
      }

      setStages(sortedStages);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.stagesLoadError"));
    }
  }, [queueId]);

  const fetchCards = useCallback(async () => {
    try {
      console.log(`[DEBUG] Buscando cartões para a fila ${queueId}`);
      const { data } = await api.get("/kanban/cards", {
        params: { queueId }
      });
      console.log(`[DEBUG] Cartões encontrados: ${data.length}`, data);

      // Exibindo informações detalhadas sobre os cartões
      if (data.length > 0) {
        data.forEach(card => {
          console.log(`[DEBUG] Detalhes do cartão: id=${card.id}, title=${card.title}, stageId=${card.stageId}, tipo=${typeof card.stageId}`);

          // Corrigir cartões com stageId nulo - buscar estágios se ainda não tivermos carregado
          if (card.stageId === null || card.stageId === undefined) {
            console.log(`[DEBUG] Cartão ${card.id} tem stageId nulo, será atribuído a um estágio inicial`);
          }
        });
      }

      setCards(data);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.cardsLoadError"));
    } finally {
      setLoading(false);
    }
  }, [queueId]);

  // Buscar usuários para o filtro de responsáveis
  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      // Garantir que data seja um array válido
      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        console.log("[DEBUG] Resposta da API de usuários não é um array:", data);
        setUsers([]); // Inicializar como array vazio em caso de erro
      }
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
      setUsers([]); // Garantir que seja um array vazio em caso de erro
    }
  }, []);

  // Extrair tags únicas dos cartões para o filtro
  const extractUniqueTags = useCallback((cardsList) => {
    if (!Array.isArray(cardsList) || cardsList.length === 0) return [];

    const tagsSet = new Set();

    cardsList.forEach(card => {
      if (card.tags && typeof card.tags === 'string' && card.tags.trim() !== '') {
        const tagArray = card.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
        tagArray.forEach(tag => {
          if (tag) tagsSet.add(tag);
        });
      }
    });

    console.log("[DEBUG] Tags extraídas:", Array.from(tagsSet));
    return Array.from(tagsSet).sort();
  }, []);

  // Filtrar cartões com base nos critérios selecionados
  const filterCards = useCallback(() => {
    let filtered = [...cards];

    // Filtrar por texto de pesquisa (título, descrição ou contato)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(card => (
        (card.title && card.title.toLowerCase().includes(searchLower)) ||
        (card.description && card.description.toLowerCase().includes(searchLower)) ||
        (card.ticket && card.ticket.contact &&
          card.ticket.contact.number && card.ticket.contact.number.includes(searchLower))
      ));
    }

    // Filtrar por tags selecionadas
    if (selectedTags.length > 0) {
      filtered = filtered.filter(card => {
        if (!card.tags) return false;

        const cardTags = card.tags.split(',').map(tag => tag.trim());
        return selectedTags.some(tag => cardTags.includes(tag));
      });
    }

    // Filtrar por prioridade
    if (selectedPriority) {
      filtered = filtered.filter(card => card.priority === selectedPriority);
    }

    // Filtrar por responsável
    if (selectedUser) {
      if (selectedUser === "null") {
        // Filtrar cartões sem responsável atribuído
        filtered = filtered.filter(card => !card.userId || card.userId === null);
      } else {
        // Filtrar por ID do usuário
        filtered = filtered.filter(card => card.userId === parseInt(selectedUser));
      }
    }

    setFilteredCards(filtered);
  }, [cards, searchText, selectedTags, selectedPriority, selectedUser]);

  const loadKanbanData = useCallback(async () => {
    setLoading(true);
    try {
      // Primeiro carregar as configurações e estágios
      await fetchSettings();
      await fetchStages();
      
      // Carregar usuários e cartões em paralelo para otimizar
      const [usersResult, cardsResult] = await Promise.all([
        api.get("/users"),
        api.get("/kanban/cards", { params: { queueId } })
      ]);
      
      // Processar usuários
      if (Array.isArray(usersResult.data)) {
        console.log("[DEBUG] Usuários carregados:", usersResult.data.length);
        setUsers(usersResult.data);
      } else {
        console.error("[DEBUG] Resposta da API de usuários não é um array:", usersResult.data);
        setUsers([]);
      }
      
      // Processar cartões
      if (Array.isArray(cardsResult.data)) {
        console.log("[DEBUG] Cartões carregados:", cardsResult.data.length);
        setCards(cardsResult.data);
        
        // Extrair tags imediatamente após carregar os cartões
        const tags = extractUniqueTags(cardsResult.data);
        console.log("[DEBUG] Tags extraídas no carregamento:", tags);
        setUniqueTags(tags);
      }
    } catch (err) {
      console.error("Erro ao carregar dados do Kanban:", err);
      toast.error(i18n.t("kanban.notifications.loadError"));
    } finally {
      setLoading(false);
    }
  }, [fetchSettings, fetchStages, extractUniqueTags, queueId]);

  useEffect(() => {
    loadKanbanData();
  }, [loadKanbanData]);

  useEffect(() => {
    // Extrair tags únicas sempre que os cartões forem atualizados
    if (Array.isArray(cards) && cards.length > 0) {
      const tags = extractUniqueTags(cards);
      console.log(`[DEBUG] Tags únicas extraídas após atualização: ${tags.length}`, tags);
      setUniqueTags(tags);
      
      // Log detalhado para ver quais cartões têm responsáveis atribuídos
      console.log("[DEBUG] Responsáveis nos cartões:", 
        cards.map(card => ({ 
          id: card.id, 
          title: card.title, 
          userId: card.userId, 
          userName: card.user ? card.user.name : "Sem responsável" 
        }))
      );
    } else {
      setUniqueTags([]);
    }
  }, [cards, extractUniqueTags]);

  useEffect(() => {
    // Aplicar filtros sempre que os critérios ou os cartões mudarem
    filterCards();
  }, [filterCards, cards, searchText, selectedTags, selectedPriority, selectedUser]);

  // Atualizar os cartões filtrados inicialmente quando os cartões são carregados
  useEffect(() => {
    setFilteredCards(cards);
  }, [cards]);

  const handleDragEnd = async result => {
    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) {
      return;
    }

    // Dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const cardId = parseInt(draggableId.split("-")[1]);
    const newStageId = parseInt(destination.droppableId.split("-")[1]);

    try {
      // Update card's stage in the backend
      await api.put(`/kanban/cards/${cardId}/move`, {
        stageId: newStageId
      });

      // Update local state
      const updatedCards = cards.map(card => {
        if (card.id === cardId) {
          return { ...card, stageId: newStageId };
        }
        return card;
      });

      setCards(updatedCards);

      toast.success(i18n.t("kanban.notifications.cardMoved"));

    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.cardMoveError"));
      // Reload data to ensure UI consistency
      loadKanbanData();
    }
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    fetchSettings();
  };

  const handleNewStageOpen = () => {
    setNewStageOpen(true);
  };

  const handleNewStageClose = () => {
    setNewStageOpen(false);
    fetchStages();
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value || '');
  };

  const handleTagSelect = (event) => {
    if (!event || !event.target) return;
    const value = event.target.value;
    setSelectedTags(typeof value === 'string' ? value.split(',') : (Array.isArray(value) ? value : []));
  };

  const handlePrioritySelect = (event) => {
    if (!event || !event.target) return;
    setSelectedPriority(event.target.value || '');
  };

  const handleUserSelect = (event) => {
    if (!event || !event.target) return;
    setSelectedUser(event.target.value || '');
  };

  const clearFilters = () => {
    setSearchText('');
    setSelectedTags([]);
    setSelectedPriority('');
    setSelectedUser('');
  };

  if (loading) {
    return (
      <Paper className={classes.root}>
        <Typography variant="h6" align="center">
          {i18n.t("kanban.loading")}
        </Typography>
      </Paper>
    );
  }

  if (!kanbanSettings || !kanbanSettings.enabled) {
    return (
      <Paper className={classes.root}>
        <Grid
          container
          direction="column"
          justify="center"
          alignItems="center"
          style={{ height: "100%" }}
        >
          <Typography variant="h6" align="center" gutterBottom>
            {i18n.t("kanban.disabled")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSettingsOpen}
          >
            {i18n.t("kanban.enableButton")}
          </Button>
          <KanbanSettingsDialog
            open={settingsOpen}
            onClose={handleSettingsClose}
            queueId={queueId}
            settings={kanbanSettings}
          />
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <div className={classes.actionButtons}>
          <Can
            role={user?.profile}
            perform="kanban-settings:manage"
            yes={() => (
              <>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={handleNewStageOpen}
                >
                  {i18n.t("kanban.newStage")}
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={handleSettingsOpen}
                >
                  {i18n.t("kanban.settings.title")}
                </Button>
              </>
            )}
            no={() => <div />} // Renderiza um div vazio quando o usuário não tem permissão
          />
        </div>
       {/* Área de Filtros */}
       <div className={classes.filtersContainer}>
        <TextField
          className={classes.searchInput}
          variant="outlined"
          size="small"
          placeholder={i18n.t("kanban.filters.searchPlaceholder") || "Buscar por título, descrição ou contato"}
          value={searchText}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchText("")}
                  aria-label="clear search"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <FormControl variant="outlined" size="small" className={classes.filterInput}>
          <InputLabel id="priority-select-label">
            {i18n.t("kanban.filters.priority") || "Prioridade"}
          </InputLabel>
          <Select
            labelId="priority-select-label"
            value={selectedPriority}
            onChange={handlePrioritySelect}
            label={i18n.t("kanban.filters.priority") || "Prioridade"}
          >
            <MenuItem value="">
              <em>{i18n.t("kanban.filters.all") || "Todos"}</em>
            </MenuItem>
            <MenuItem value="low">{i18n.t("kanban.priorities.low") || "Baixa"}</MenuItem>
            <MenuItem value="medium">{i18n.t("kanban.priorities.medium") || "Média"}</MenuItem>
            <MenuItem value="high">{i18n.t("kanban.priorities.high") || "Alta"}</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterInput}>
          <InputLabel id="tags-select-label">
            {i18n.t("kanban.filters.tags") || "Tags"}
          </InputLabel>
          <Select
            labelId="tags-select-label"
            multiple
            value={selectedTags}
            onChange={handleTagSelect}
            label={i18n.t("kanban.filters.tags") || "Tags"}
            renderValue={(selected) => (
              <div className={classes.chips}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    className={classes.filterChip}
                    size="small"
                  />
                ))}
              </div>
            )}
          >
            <MenuItem value="">
              <em>{i18n.t("kanban.filters.all") || "Todos"}</em>
            </MenuItem>
            {Array.isArray(uniqueTags) && uniqueTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterInput}>
          <InputLabel id="user-select-label">
            {i18n.t("kanban.filters.responsible") || "Responsável"}
          </InputLabel>
          <Select
            labelId="user-select-label"
            value={selectedUser}
            onChange={handleUserSelect}
            label={i18n.t("kanban.filters.responsible") || "Responsável"}
          >
            <MenuItem value="">
              <em>{i18n.t("kanban.filters.all") || "Todos"}</em>
            </MenuItem>
            <MenuItem value="null">
              {i18n.t("kanban.noResponsible") || "Sem responsável"}
            </MenuItem>
            {Array.isArray(users) && users.map((user) => (
              <MenuItem key={user.id} value={user.id.toString()}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(searchText || selectedTags.length > 0 || selectedPriority || selectedUser) && (
          <Button
            variant="outlined"
            size="small"
            onClick={clearFilters}
            style={{ marginLeft: 8 }}
          >
            {i18n.t("kanban.filters.clearFilters") || "Limpar Filtros"}
          </Button>
        )}
      </div>

      </div>
      
    


      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={classes.boardContainer}>
          <Droppable
            droppableId="all-stages"
            direction="horizontal"
            type="stage"
          >
            {provided => (
              <div
                className={classes.stagesContainer}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {stages.map((stage, index) => {
                  // Identificar estágio inicial
                  const isInitialStage = stage.isInitial === true;

                  // Filtrar apenas os cartões que pertencem a este estágio e que passaram pelos filtros
                  const stageCards = filteredCards.filter(card => {
                    if (card.stageId === null || card.stageId === undefined) {
                      // Atribuir cartões sem estágio ao estágio inicial
                      return isInitialStage;
                    }

                    // Converter ambos para string para comparação segura
                    const cardStageId = card.stageId.toString();
                    const currentStageId = stage.id.toString();
                    return cardStageId === currentStageId;
                  });

                  return (
                    <KanbanStage
                      key={stage.id}
                      stage={stage}
                      cards={stageCards}
                      index={index}
                      onCardUpdate={fetchCards}
                      onStageUpdate={fetchStages}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>

      <KanbanSettingsDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        queueId={queueId}
        settings={kanbanSettings}
      />

      <NewStageDialog
        open={newStageOpen}
        onClose={handleNewStageClose}
        queueId={queueId}
        existingStages={stages}
      />
    </Paper>
  );
};

export default KanbanBoard;